import * as Turbo from "@hotwired/turbo";
import * as ActiveStorage from "@rails/activestorage";
import * as Sentry from "@sentry/browser";
import "@/javascript/stimulus_lazy_loader";
import "slim-select/styles";
import { initTracking } from "@/javascript/tracking/user_interaction";

// Loopback names, plus the RFC1918 ranges and mDNS suffix a dev server bound to
// a LAN address is reached on. Matching only the literal "localhost" meant that
// running the dev server on a machine's network IP reported local browser errors
// into the production Sentry project.
const LOCAL_HOSTNAMES = /^(localhost|0\.0\.0\.0|127\.0\.0\.1|\[::1\]|::1)$/;
const PRIVATE_IPV4 = /^(10\.|127\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

function isLocalHostname(hostname) {
  return (
    LOCAL_HOSTNAMES.test(hostname) || PRIVATE_IPV4.test(hostname) || hostname.endsWith(".local")
  );
}

function getSentryEnvironment() {
  const hostname = window.location.hostname;
  if (isLocalHostname(hostname)) {
    return null;
  }
  if (hostname.includes("staging")) {
    return "staging";
  }
  return "production";
}

const sentryEnvironment = getSentryEnvironment();
if (sentryEnvironment) {
  Sentry.init({
    dsn: "https://1dadd9a1b3804179a1411bef0ebc0fd3@o4505042925715456.ingest.sentry.io/4505042928533504",
    environment: sentryEnvironment,
    ignoreErrors: [
      /Unable to preload CSS/i,
      /Failed to fetch dynamically imported module/i,
      /ChunkLoadError/i
    ],
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? "";
      if (
        message.includes("Unable to preload CSS") ||
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("ChunkLoadError")
      ) {
        return null;
      }
      return event;
    }
  });
  window.Sentry = Sentry;
} else {
  window.Sentry = null;
}

window.addEventListener("vite:preloadError", () => {
  window.location.reload();
});

ActiveStorage.start();

Turbo.start();

document.addEventListener("turbo:fetch-request-error", (event) => {
  const frame = event.target;
  if (!frame?.id?.includes("vr4_nearby_properties")) return;

  event.preventDefault();
  if (window.Sentry) {
    window.Sentry.captureException(new Error("Turbo Frame fetch failed"), {
      tags: { component: "vr4_nearby_properties", method: "turbo_frame_load" },
      extra: {
        frameId: frame.id,
        frameSrc: frame.src,
        error: event.detail?.error || "Unknown error"
      }
    });
  }
});

// load global js
import.meta.glob("../javascript/global/*.js", { eager: true });
// Load Custom JS
import.meta.glob("../javascript/custom/*.js", { eager: true });
// init user tracking
initTracking();
